import React, { useState, useEffect } from 'react'
import { IoClose } from "react-icons/io5";
import { IoEye, IoEyeOff } from "react-icons/io5";
import axios from 'axios';
import { API } from './constant';
import CreatableSelect from 'react-select/creatable';
import { toast, ToastContainer } from 'react-toastify';

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString();
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

const AccountDetails = () => {

  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accountList, setAccountList] = useState([]);
  const [editingAccount, setEditingAccount] = useState(null);
  const [companyNameOptions, setCompanyNameOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleRowId, setVisibleRowId] = useState(null);

  const maskValue = (value) => {
    if (!value) return "N/A";
    if (value.length <= 3) return "***";
    return value.slice(0, 2) + "*".repeat(value.length - 2);
  };

  const handleAuthError = (error) => {
    if (error.response?.status === 401 || error.response?.data?.message === "Invalid Token") {
      window.location.href = "/";
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/account-details`);
      setAccountList(response.data);
    } catch (error) {
      console.error("Error fetching account details:", error);
      handleAuthError(error);
    }
  };

  const fetchCompanyNames = async () => {
    try {
      const response = await axios.get(`${API}/account-details/company-names`);
      setCompanyNameOptions(response.data);
    } catch (error) {
      console.error("Error fetching company names:", error);
      handleAuthError(error);
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchCompanyNames();
  }, []);

  const handleAccountSubmit = async (data) => {
    try {
      if (editingAccount) {
        await axios.put(`${API}/account-details/${editingAccount._id}`, data);
      } else {
        await axios.post(`${API}/account-details`, data);
      }
      fetchAccounts();
      fetchCompanyNames();
      setShowAccountModal(false);
      setEditingAccount(null);
    } catch (error) {
      if (error.response) {
        toast.warning(error.response.data.message || "Something went wrong")
      } else if (error.request) {
        toast.warning("Server not responding, please try again later")
      } else {
        toast.warning("Unexpected error occurred")
      }
      handleAuthError(error);
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setShowAccountModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this account record?")) {
      try {
        await axios.delete(`${API}/account-details/${id}`);
        fetchAccounts();
      } catch (error) {
        console.error("Error deleting account:", error);
        handleAuthError(error);
      }
    }
  };

  const handleCloseModal = () => {
    setShowAccountModal(false);
    setEditingAccount(null);
  }

  const filteredAccounts = accountList.filter((account) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;
    const companyName = (account.companyName || "").toLowerCase();
    const applicationName = (account.applicationName || "").toLowerCase();
    return companyName.includes(query) || applicationName.includes(query);
  });

  return (
    <div>
      <div>
        <ToastContainer autoClose={1000} />
        <div className='card_button_container'>
          <div className='button_search DFJCBAIC marginB10'>
            <div></div>
            <div className='DFJCCAIC gap10'>
              <div>
                <button onClick={() => setShowAccountModal(true)} className='add_button'>Add Account</button>
              </div>
              <div>
                <input
                  className='search_box'
                  type="search"
                  placeholder='Filter'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          <hr />

          <div className='table_container width100'>
            <table className="mac_table">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Application Name</th>
                  <th>Service URL</th>
                  <th>Renewal Date</th>
                  <th>Expiration Date</th>
                  <th>Login Email</th>
                  <th>Login Password</th>
                  <th>Notification Email</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredAccounts.map((item) => (
                  <tr key={item._id}>
                    <td>{item.companyName}</td>
                    <td>{item.applicationName}</td>
                    <td>
                      {item.serviceUrl
                        ? <a href={item.serviceUrl} target="_blank" rel="noopener noreferrer">{item.serviceUrl}</a>
                        : "N/A"}
                    </td>
                    <td>{formatDate(item.renewalDate)}</td>
                    <td>{formatDate(item.expirationDate)}</td>
                    <td>{item.loginEmail}</td>
                    <td>
                      <div className='DFJCCAIC gap5'>
                        <span>
                          {visibleRowId === item._id ? item.loginPassword : maskValue(item.loginPassword)}
                        </span>
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => setVisibleRowId(visibleRowId === item._id ? null : item._id)}
                        >
                          {visibleRowId === item._id ? <IoEyeOff size={16} /> : <IoEye size={16} />}
                        </span>
                      </div>
                    </td>
                    <td>{item.notificationEmail}</td>
                    <td>
                      <button onClick={() => handleEdit(item)} style={{ marginRight: "10px", cursor: "pointer" }}>Edit</button>
                      <button onClick={() => handleDelete(item._id)} style={{ cursor: "pointer", color: "red" }}>Delete</button>
                    </td>
                  </tr>
                ))}
                {filteredAccounts.length === 0 && (
                  <tr>
                    <td colSpan="9" style={{ textAlign: "center" }}>No account details found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {showAccountModal && (
            <AddAccountModal
              onClose={handleCloseModal}
              onSubmit={handleAccountSubmit}
              companyNameOptions={companyNameOptions}
              initialData={editingAccount}
            />
          )}

        </div>
      </div>
    </div>
  )
}


const AddAccountModal = ({ onClose, onSubmit, companyNameOptions = [], initialData }) => {

  const [formData, setFormData] = useState({
    companyName: "",
    applicationName: "",
    serviceUrl: "",
    renewalDate: "",
    expirationDate: "",
    loginEmail: "",
    loginPassword: "",
    notificationEmail: ""
  });

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        companyName: initialData.companyName || "",
        applicationName: initialData.applicationName || "",
        serviceUrl: initialData.serviceUrl || "",
        renewalDate: toDateInputValue(initialData.renewalDate),
        expirationDate: toDateInputValue(initialData.expirationDate),
        loginEmail: initialData.loginEmail || "",
        loginPassword: initialData.loginPassword || "",
        notificationEmail: initialData.notificationEmail || ""
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCompanyNameChange = (selectedOption) => {
    setFormData({
      ...formData,
      companyName: selectedOption ? selectedOption.value : ""
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-center items-center z-50">

      <div className="bg-white w-[520px] rounded-xl shadow-lg overflow-hidden">

        <div className="flex justify-between items-center px-4 py-3 border-b bg-[#f5f5f7]">
          <h2 className="text-sm font-medium">{initialData ? "Edit Account Details" : "Add Account Details"}</h2>
          <IoClose size={20} className="cursor-pointer" onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3 text-sm max-h-[70vh] overflow-y-auto">

          <div className="form_group">
            <label>Company Name</label>
            <CreatableSelect
              name="companyName"
              value={formData.companyName ? { value: formData.companyName, label: formData.companyName } : null}
              onChange={handleCompanyNameChange}
              options={companyNameOptions.map((name) => ({ value: name, label: name }))}
              placeholder="Select or type a company name"
              isClearable
              className="w-full"
            />
          </div>

          <div className="form_group">
            <label>Application Name</label>
            <input
              type="text"
              name="applicationName"
              placeholder="Application Name"
              value={formData.applicationName}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
              required
            />
          </div>

          <div className="form_group">
            <label>Service URL</label>
            <input
              type="url"
              name="serviceUrl"
              placeholder="https://example.com/renew"
              value={formData.serviceUrl}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div className="form_group">
            <label>Renewal Date</label>
            <input
              type="date"
              name="renewalDate"
              value={formData.renewalDate}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div className="form_group">
            <label>Expiration Date</label>
            <input
              type="date"
              name="expirationDate"
              value={formData.expirationDate}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
              required
            />
          </div>

          <div className="form_group">
            <label>Login Email</label>
            <input
              type="email"
              name="loginEmail"
              placeholder="Registered login email"
              value={formData.loginEmail}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
              required
            />
          </div>

          <div className="form_group">
            <label>Login Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="loginPassword"
                placeholder="Registered login password"
                value={formData.loginPassword}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
                required
              />
              <span
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <IoEyeOff size={16} /> : <IoEye size={16} />}
              </span>
            </div>
          </div>

          <div className="form_group">
            <label>Notification Email (expiry alerts)</label>
            <input
              type="email"
              name="notificationEmail"
              placeholder="Email to receive renewal reminders"
              value={formData.notificationEmail}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2"
              required
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="white_button"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="add_button"
            >
              {initialData ? "Update" : "Save"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};


export default AccountDetails
